import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppState } from "../../context/AppStateContext";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useShift } from "../../hooks/queries";
import { useLocale } from "../../hooks/useLocale";
import { useNow } from "../../hooks/useNow";
import { formatTime, initials } from "../../lib/format";
import { hasNativeHardware, openCashDrawer } from "../../lib/hardware";
import { Logo } from "../ui/Logo";
import { LanguageSwitch } from "./LanguageSwitch";
import { PrinterPill } from "./PrinterPill";
import { ShiftDialog } from "../admin/ShiftDialog";

export function Header() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { user, logout, branch, selectBranch } = useAuth();
  const { setTab } = useAppState();
  const { toast, toastError } = useToast();
  const now = useNow();
  const shift = useShift();
  const [shiftOpen, setShiftOpen] = useState(false);

  async function drawer() {
    try {
      const r = await openCashDrawer();
      toast(r === "native" ? t("header.drawerOpened") : t("header.drawerUnavailable"), r === "native" ? "success" : "warning");
    } catch (e) {
      toastError(e);
    }
  }

  const open = shift.data?.shift;
  return (
    <header className="sticky top-0 z-50 border-b border-[#202552] bg-cosmic-navy px-5 py-3 text-white shadow-lg">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Logo variant="darkWordmark" className="h-11" />
          <p className="hidden items-center gap-1 text-[11px] font-medium text-[#A0A5BD] sm:flex">
            <span className="inline-block h-2 w-2 animate-ping rounded-full bg-emerald-400" />
            {t("brand.station")}
          </p>
        </div>

        <div className="hidden items-center gap-4 rounded-2xl border border-[#23285C] bg-[#161A42] px-4 py-2 lg:flex">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t("header.stationTime")}</div>
            <div className="num font-mono text-base font-black tracking-wider text-emerald-400">{formatTime(new Date(now), locale)}</div>
          </div>
          <div className="h-7 w-px bg-[#262C66]" />
          <button type="button" onClick={() => setShiftOpen(true)} className="text-start" title={t("shift.manage")}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t("header.activeShift")}</div>
            <div className="flex items-center gap-1 text-xs font-bold">
              {open ? (
                <>
                  <span className="text-solar-orange">☀️</span> {t("header.shiftSince", { time: formatTime(open.openedAt, locale) })} • <span className="num">{open.terminal}</span>
                </>
              ) : (
                <span className="text-amber-300">{t("header.noShift")}</span>
              )}
            </div>
          </button>
        </div>

        <div className="flex items-center gap-2.5">
          <button type="button" onClick={drawer} className="flex h-10 items-center gap-2 rounded-xl border border-[#2B357A] bg-[#191F4D] px-3.5 text-xs font-bold hover:bg-[#252E6E]" title={hasNativeHardware() ? undefined : t("header.drawerUnavailable")}>
            <span aria-hidden>🗄️</span>
            <span className="hidden sm:inline">{t("header.openDrawer")}</span>
          </button>
          <button type="button" onClick={() => setTab("cashier")} className="flex h-10 items-center gap-1.5 rounded-xl bg-gradient-to-r from-cosmic-purple to-cosmic-glow px-4 text-xs font-black shadow-cosmic hover:opacity-95">
            <span aria-hidden>⚡</span>
            <span>{t("header.quickTicket")}</span>
          </button>
          {branch && (user?.branches?.length ?? 0) > 1 ? (
            <select aria-label={t("branch.switch")} className="rounded-xl border border-[#2B357A] bg-[#191F4D] px-2 py-2 text-xs font-bold" value={branch.id} onChange={(e) => selectBranch(e.target.value)}>
              {user!.branches!.map((b) => <option key={b.id} value={b.id}>{locale === "ar" && b.name_ar ? b.name_ar : b.name}</option>)}
            </select>
          ) : (
            branch && <span className="num hidden rounded-lg bg-[#191F4D] px-2 py-1 font-mono text-[11px] font-bold text-purple-200 md:inline" title={t("branch.current")}>{branch.code}</span>
          )}
          <PrinterPill />
          <div className="hidden md:block"><LanguageSwitch dark /></div>
          <div className="flex items-center gap-2 border-s border-[#262C66] ps-2">
            <div className="relative h-9 w-9 rounded-xl bg-gradient-to-b from-cosmic-purple to-[#391896] p-0.5">
              <div className="flex h-full w-full items-center justify-center rounded-[10px] bg-[#12163E] text-xs font-black text-solar-orange">{initials(user?.name ?? "")}</div>
              <span className="absolute -bottom-0.5 -end-0.5 h-2.5 w-2.5 rounded-full border-2 border-cosmic-navy bg-emerald-400" />
            </div>
            <div className="hidden text-start md:block">
              <div className="text-xs font-black leading-tight">{user?.name}</div>
              <div className="text-[10px] font-semibold uppercase text-purple-300">{t(`roles.${user?.role ?? "cashier"}`)}</div>
            </div>
            <button type="button" onClick={logout} className="rounded-lg px-2 py-1 text-[11px] font-bold text-gray-400 hover:bg-white/10 hover:text-white">{t("header.logout")}</button>
          </div>
        </div>
      </div>
      <ShiftDialog open={shiftOpen} onClose={() => setShiftOpen(false)} />
    </header>
  );
}
